import { useState } from 'react'
import { BackfillAccountButton, BackfillProgressLine } from './backfill-account-button'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { MessageCircle, Plus, RefreshCw, LogIn, Unplug, Trash2, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import { Loading, ErrorState, EmptyState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import {
  useZaloAccounts,
  useConnectZalo,
  useZaloLogin,
  useZaloReconnect,
  usePauseZaloAccount,
  useDeleteZaloAccount,
  statusMeta,
  type ChannelAccount,
} from '@/hooks/use-integrations'
import { useAuthStore } from '@/stores/auth-store'
import { ChannelCard } from './channel-card'
import { BusinessSwitch } from './business-badge'
import { ReportSwitch } from './report-switch'
import { QrLoginDialog } from './qr-login-dialog'

export function ZaloPersonalTab() {
  // includeDisabled: tài khoản đang tạm dừng vẫn phải nằm trong danh sách,
  // nếu không thì không còn chỗ nào bấm "Kết nối lại".
  const { data, isLoading, isError } = useZaloAccounts('personal', false, true)
  const role = useAuthStore((s) => s.user?.role)
  const suaDuoc = role === 'owner' || role === 'admin'
  const connect = useConnectZalo()
  const login = useZaloLogin()
  const reconnect = useZaloReconnect()
  const pause = usePauseZaloAccount()
  const remove = useDeleteZaloAccount()

  const [qrAccountId, setQrAccountId] = useState<string | null>(null)
  const [qrOpen, setQrOpen] = useState(false)
  const [toDelete, setToDelete] = useState<ChannelAccount | null>(null)

  const openQr = (accountId: string) => {
    setQrAccountId(accountId)
    setQrOpen(true)
  }

  const handleConnect = () => {
    connect.mutate(undefined, {
      onSuccess: (res) => openQr(res.accountId),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const handleRelogin = (acc: ChannelAccount) => {
    login.mutate(acc.id, {
      onSuccess: () => openQr(acc.id),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const handleReconnect = (acc: ChannelAccount) => {
    reconnect.mutate(acc.id, {
      onSuccess: () => toast.success('Đang kết nối lại bằng phiên đã lưu...'),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  /** Tạm dừng: đảo ngược được bằng một nút, nên không hỏi lại. */
  const handlePause = (acc: ChannelAccount) => {
    pause.mutate(acc.id, {
      onSuccess: () => toast.success('Đã tạm dừng kết nối — bấm "Kết nối lại" khi cần dùng tiếp'),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  const handleDelete = () => {
    if (!toDelete) return
    remove.mutate(toDelete.id, {
      onSuccess: () => {
        toast.success('Đã xoá tài khoản khỏi danh sách')
        setToDelete(null)
      },
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          Kết nối tài khoản Zalo cá nhân bằng mã QR để đồng bộ hội thoại và bạn bè.
        </p>
        <Button onClick={handleConnect} disabled={connect.isPending}>
          {connect.isPending ? (
            <Loader2 className="animate-spin" />
          ) : (
            <Plus />
          )}
          Kết nối
        </Button>
      </div>

      {isLoading ? (
        <Loading label="Đang tải tài khoản Zalo..." />
      ) : isError ? (
        <ErrorState />
      ) : !data?.length ? (
        <EmptyState
          icon={MessageCircle}
          title="Chưa có tài khoản Zalo cá nhân"
          description="Nhấn Kết nối và quét mã QR bằng ứng dụng Zalo trên điện thoại."
        />
      ) : (
        <div className="space-y-3">
          {data.map((acc) => {
            const st = acc.liveStatus
            const isConnected = st === 'connected' && !acc.isDisabled
            return (
              <ChannelCard
                key={acc.id}
                icon={<MessageCircle className="h-5 w-5" />}
                avatarUrl={acc.avatarUrl}
                title={acc.displayName || acc.phone || 'Tài khoản Zalo'}
                subtitle={
                  <>
                    {/* Business hay thường quyết định hạn mức gửi, nên để ngay
                        dòng đầu chứ không giấu trong menu. */}
                    <span className="mr-1.5 inline-flex translate-y-[1px] align-middle">
                      <BusinessSwitch acc={acc} canEdit={suaDuoc} />
                    </span>
                    <span className="mr-1.5 inline-flex translate-y-[1px] align-middle">
                      <ReportSwitch acc={acc} canEdit={suaDuoc} />
                    </span>
                    {acc.phone ? `${acc.phone} · ` : ''}
                    {acc.lastConnectedAt
                      ? `Kết nối lần cuối ${dayjs(acc.lastConnectedAt).format('DD/MM/YYYY HH:mm')}`
                      : 'Chưa từng kết nối'}
                    <BackfillProgressLine accountId={acc.id} />
                  </>
                }
                status={statusMeta(st, acc.isDisabled)}
                actions={
                  <>
                    {isConnected && <BackfillAccountButton accountId={acc.id} />}
                    {!isConnected && (
                      <>
                        <Button
                          variant={acc.isDisabled ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => handleReconnect(acc)}
                          disabled={reconnect.isPending}
                          title="Kết nối lại bằng phiên đã lưu"
                        >
                          <RefreshCw /> Kết nối lại
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRelogin(acc)}
                          disabled={login.isPending}
                        >
                          <LogIn /> Đăng nhập QR
                        </Button>
                      </>
                    )}
                    {/* Tạm dừng và Xoá là hai việc khác hẳn nhau, nên là hai
                        nút. Trước đây chỉ có một nút hình phích cắm nhưng lại
                        gọi lệnh xoá — bấm nhầm là nick rời khỏi danh sách. */}
                    {!acc.isDisabled && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handlePause(acc)}
                        disabled={pause.isPending}
                        title="Tạm dừng kết nối — tài khoản vẫn nằm trong danh sách"
                      >
                        <Unplug />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setToDelete(acc)}
                      title="Xoá khỏi danh sách"
                    >
                      <Trash2 />
                    </Button>
                  </>
                }
              />
            )
          })}
        </div>
      )}

      <QrLoginDialog accountId={qrAccountId} open={qrOpen} onOpenChange={setQrOpen} />

      <Dialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Xoá tài khoản khỏi danh sách?</DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-2 text-sm">
                <p className="font-medium text-foreground">
                  {toDelete?.displayName || toDelete?.phone || 'Tài khoản Zalo'}
                </p>
                <p>
                  Phiên đăng nhập bị ngắt và tài khoản <strong>biến mất khỏi màn Tích hợp</strong>.
                  Muốn dùng lại thì phải quét mã QR kết nối từ đầu.
                </p>
                <p>
                  Hội thoại và tin nhắn cũ <strong>không bị xoá</strong> — vẫn xem lại được
                  trong màn Chat bằng bộ lọc “Bao gồm nick đã gỡ”, nhưng không nhắn tiếp được.
                </p>
                <p className="rounded-md bg-warning/10 px-3 py-2 text-warning">
                  Chỉ muốn ngừng tạm thời thì đóng hộp này và bấm nút hình phích cắm —
                  tài khoản vẫn nằm trong danh sách và bấm “Kết nối lại” là chạy tiếp.
                </p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToDelete(null)}>
              Hủy
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={remove.isPending}>
              {remove.isPending && <Loader2 className="animate-spin" />}
              Xoá khỏi danh sách
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
